import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { propTypes } from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Sums the buyer-visible, non-reversal line items: the gross amount the buyer was
 * charged (item + shipping + sales tax). This is used instead of `payinTotal`
 * because `payinTotal` nets out refund reversals to zero, whereas this line is
 * meant to show what the buyer originally paid so it lines up with the refund.
 */
const buyerGrossCharged = (lineItems, marketplaceCurrency) => {
  const charged = lineItems.filter(item => !item.reversal && item.includeFor?.includes('customer'));
  const amount = charged.reduce((sum, item) => sum + item.lineTotal.amount, 0);
  const currency = charged[0] ? charged[0].lineTotal.currency : marketplaceCurrency;
  return charged.length > 0 ? new Money(amount, currency) : null;
};

/**
 * Provider-only line: the gross total the buyer was charged (item + shipping +
 * buyer sales tax). Shown as a subtotal to close the "what the buyer paid"
 * section. Informational only - the seller's payout is computed separately below
 * from the commission and its tax.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction (for its line items).
 * @param {boolean} props.isProvider - Whether the current viewer is the seller.
 * @param {string} props.marketplaceCurrency - Fallback currency when there are no line items.
 * @param {intlShape} props.intl - The intl object.
 * @returns {JSX.Element|null}
 */
const LineItemBuyerPaidTotalMaybe = props => {
  const { transaction, isProvider, marketplaceCurrency, intl } = props;
  if (!isProvider) return null;

  const lineItems = transaction?.attributes?.lineItems || [];
  const grossCharged = buyerGrossCharged(lineItems, marketplaceCurrency);
  if (!grossCharged) return null;

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.subTotalLineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.buyerPaidTotal" />
        </span>
        <span className={css.itemValue}>{formatMoney(intl, grossCharged)}</span>
      </div>
    </>
  );
};

export default LineItemBuyerPaidTotalMaybe;
